import * as React from 'react';
import * as Sentry from '@sentry/browser';
import { connect } from 'react-redux';
import { isNil } from 'lodash';

import { resetDiscoveryCreatorsFilters, updateDiscoveryCreatorsFilters } from '../reducers/creators';

import { getBrandProfileId } from 'utils/organization';
import { removeItem } from 'utils/storage/sessionStorage';
import { logError } from 'utils/log';
import { addUserAction } from 'modules/tracking/providers/user-action-provider';
import { prepareFiltersForAnalytics } from '../utils/tracking';
import { HAS_MANUALLY_SELECTED_CHANNEL_STORAGE_NAME, getChannel } from '../utils/creators';

import type {
  DiscoveryCreatorsFilters,
  Organization
} from 'types';

type OwnProps = {
  children: React.ReactNode
};

type StateProps = {
  filters: Partial<DiscoveryCreatorsFilters>,
  lastStateReset: number,
  currentOrganization: Organization
};

type DispatchProps = {
  dispatchUpdateFilters: (updatedFilters: Partial<DiscoveryCreatorsFilters>) => Promise<void>,
  dispatchResetFilters: () => void
};

type Props = OwnProps & StateProps & DispatchProps;

class DiscoveryCreatorsStateHandler extends React.PureComponent<Props> {
  handleDiscoveryFiltersUpdate = (updatedFilters: Partial<DiscoveryCreatorsFilters>): void => {
    const { dispatchUpdateFilters } = this.props;

    dispatchUpdateFilters(updatedFilters);
    this.handleDiscoveryEvents(updatedFilters);
  };

  handleDiscoveryFiltersReset = (): void => {
    const { dispatchResetFilters } = this.props;

    removeItem(HAS_MANUALLY_SELECTED_CHANNEL_STORAGE_NAME);

    dispatchResetFilters();
  };

  handleDiscoveryEvents(updatedFilters: Partial<DiscoveryCreatorsFilters>): void {
    try {
      const { currentOrganization } = this.props;
      const brandProfileId = getBrandProfileId(currentOrganization);
      const preparedAnalyticsFilters = prepareFiltersForAnalytics(updatedFilters);

      if (brandProfileId) {
        addUserAction(preparedAnalyticsFilters);
      }
    } catch (error) {
      const values = JSON.stringify(updatedFilters);
      const errorWithContext = `Next error happened: ${error} with input as ${values}`;

      Sentry.captureException(new Error(errorWithContext));
      logError(error);
    }
  }

  render(): React.ReactNode {
    const { filters, children, lastStateReset } = this.props;
    const channel = getChannel(filters.channels);

    return (
      <div className="discovery-creators-state-handler">
        {
          React.Children.map(children, child => {
            if (isNil(child)) {
              return null;
            }

            return React.cloneElement(child as React.ReactPortal, {
              filters,
              channel,
              lastStateReset,
              handleDiscoveryFiltersUpdate: this.handleDiscoveryFiltersUpdate,
              handleDiscoveryFiltersReset: this.handleDiscoveryFiltersReset
            }, null);
          })
        }
      </div>
    );
  }
}

const mapStateToProps = ({ organizations, discoveryCreators }): StateProps => ({
  currentOrganization: organizations.currentOrganization,
  filters: discoveryCreators.filters,
  lastStateReset: discoveryCreators.lastStateReset
});

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchUpdateFilters: (updatedFilters: Partial<DiscoveryCreatorsFilters>): Promise<void> => (
    dispatch(updateDiscoveryCreatorsFilters(updatedFilters))
  ),
  dispatchResetFilters: () => (
    dispatch(resetDiscoveryCreatorsFilters())
  )
});

export default connect(mapStateToProps, mapDispatchToProps)(DiscoveryCreatorsStateHandler);
